import axios, {
  isAxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from 'axios';

import { HttpClientError } from '@errors/httpClient.error';
import { UnknownError } from '@errors/unknown.error';

import { interceptors } from './interceptors';

const REQUEST_TIMEOUT_MS = 30000;

class BackendHttpClient {
  private readonly instance: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.VITE_VOICE_AGENT_BACKEND_URL;

    if (!baseURL) {
      throw new Error(
        'Missing VITE_VOICE_AGENT_BACKEND_URL environment variable',
      );
    }

    this.instance = axios.create({
      baseURL,
      timeout: REQUEST_TIMEOUT_MS,
      paramsSerializer: {
        indexes: null,
      },
    });
    interceptors.setTokens(this.instance);
    // interceptors.refresh(this.instance);
  }

  public async get<Response = unknown, Params = unknown>({
    headers = {},
    params,
    path,
  }: {
    headers?: Record<string, string>;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.get<Response>(path, {
        params,
        headers: {
          ...headers,
        },
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async post<Response, Body = unknown, Params = unknown>({
    body,
    params,
    path,
  }: {
    body?: Body;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.post<Response>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async put<Response, Body = unknown, Params = unknown>({
    body,
    params,
    path,
  }: {
    body?: Body;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.put<Response>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async patch<Response, Body = unknown, Params = unknown>({
    body,
    params,
    path,
  }: {
    body?: Body;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.patch<Response>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async delete<Response, Params = unknown>({
    params,
    path,
  }: {
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.delete<Response>(path, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public removeBearerToken(): void {
    this.instance.defaults.headers.common.Authorization = '';
  }

  public setBearerToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  private throwError(err: unknown): never {
    if (isAxiosError(err)) {
      const responseStatus = err.response?.status;
      const responseStatusText = err.response?.statusText;
      const message = responseStatus
        ? `${responseStatus}${responseStatusText ? ` - ${responseStatusText}` : ''}`
        : err.code === 'ECONNABORTED'
          ? 'The API request timed out. Please try again.'
          : err.message || 'The API request failed.';

      throw new HttpClientError({
        context: { code: err.code },
        message,
        response: err.response,
      });
    }

    throw new UnknownError({
      context: {
        originalError: err,
      },
    });
  }
}

export default new BackendHttpClient();
